import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { Bars } from "react-loader-spinner";
import CustomDatePicker from "../CustomDate/CustomDatePicker.jsx";
import axiosInstance from "../../Shared/axiosInstance"; // أو المسار الصحيح




export default function UpdatePurchase() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal & search states
  const [showModal, setShowModal] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(false);

  // ✅ تحميل الموردين
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const { data } = await axiosInstance.get("/Supplier/all", {
          params: { pageNumber: 1, pageSize: 15 },
        });
        setSuppliers(data.data || []);
      } catch (err) {
        console.error("خطأ في تحميل الموردين:", err);
        Swal.fire("خطأ", "فشل في تحميل الموردين", "error");
      }
    };
    fetchSuppliers();
  }, []);

  // ✅ Formik
  const formik = useFormik({
    initialValues: {
      supplierId: "",
      purchaseDate: new Date().toISOString().slice(0, 10),
      items: [{ productId: "", productName: "", productDescription: "", quantity: 0, buyPrice: 0 }],
      payments: [{ amount: 0, date: new Date().toISOString().slice(0, 10), method: "Cash" }],
      discountAmount: 0, // ✅ إضافة الخصم
    },
    validationSchema: Yup.object({
      supplierId: Yup.string().required("المورد مطلوب"),
      items: Yup.array().of(
        Yup.object({
          productId: Yup.string().required("المنتج مطلوب"),
          quantity: Yup.number()
            .required("الكمية مطلوبة")
            .min(1, "الكمية يجب أن تكون على الأقل 1"),
          buyPrice: Yup.number()
            .required("سعر الشراء مطلوب")
            .min(0, "سعر الشراء لا يمكن أن يكون سالباً"),
        })
      ),
      payments: Yup.array().of(
        Yup.object({
          amount: Yup.number()
            .required("المبلغ مطلوب")
            .min(0, "المبلغ لا يمكن أن يكون سالباً"),
          date: Yup.string().required("التاريخ مطلوب"),
          method: Yup.string().required("طريقة الدفع مطلوبة"),
        })
      ),
      discountAmount: Yup.number().min(0, "الخصم لا يمكن أن يكون سالباً"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          id,
          supplierId: values.supplierId,
          purchaseDate: values.purchaseDate,
          items: values.items.map((item) => ({
            productId: item.productId,
            quantity: parseInt(item.quantity) || 0,
            buyPrice: parseFloat(item.buyPrice) || 0,
          })),
          payments: values.payments.map((p) => ({
            amount: parseFloat(p.amount) || 0,
            date: p.date,
            method: p.method,
          })),
          discountAmount: parseFloat(values.discountAmount) || 0, // ✅ إرسال الخصم
        };

        const { data } = await axiosInstance.put(`/Purchase/${id}`, payload);
        await Swal.fire({
          title: "نجاح!",
          text: data.message || "تم تحديث الشراء بنجاح",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        navigate("/purchases");
      } catch (err) {
        console.error("خطأ:", err);
        Swal.fire(
          "خطأ",
          err.response?.data?.message || "حدث خطأ ما",
          "error"
        );
      }
    },
  });

  // ✅ تحميل بيانات الشراء الموجودة
  useEffect(() => {
    const fetchPurchaseDetails = async () => {
      try {
        setLoading(true);
        const { data } = await axiosInstance.get(`/Purchase/get-purchase/${id}`);
        const purchase = data.data;

        formik.setValues({
          supplierId: purchase.supplierId || "",
          purchaseDate: purchase.purchaseDate?.slice(0, 10) || new Date().toISOString().slice(0, 10),
          items: purchase.items?.length
            ? purchase.items.map((item) => ({
                productId: item.productId || "",
                productName: item.productName || "",
                productDescription: item.productDescription || "",
                quantity: item.quantity || 0,
                buyPrice: item.unitPrice || 0,
              }))
            : [{ productId: "", productName: "", productDescription: "", quantity: 0, buyPrice: 0 }],
          payments: purchase.payments?.length
            ? purchase.payments.map((p) => ({
                amount: p.amount || 0,
                date: p.date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
                method: p.method || "Cash",
              }))
            : [{ amount: 0, date: new Date().toISOString().slice(0, 10), method: "Cash" }],
          discountAmount: purchase.discountAmount || 0, // ✅ تحميل الخصم
        });

        setLoading(false);
      } catch (err) {
        console.error("خطأ في تحميل تفاصيل الشراء:", err);
        Swal.fire("خطأ", "فشل في تحميل تفاصيل الشراء", "error");
        setLoading(false);
        navigate("/purchases");
      }
    };

    if (id) fetchPurchaseDetails();
  }, [id]);

  // 🔹 جلب المنتجات للـ modal مع البحث + debounce
  useEffect(() => {
    if (!showModal) return;

    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const { data } = await axiosInstance.get("/Product", {
          params: { pageNumber: 1, pageSize: 15, name: productSearch || undefined },
        });
        setProducts(data.data || []);
      } catch (err) {
        console.error("خطأ في تحميل المنتجات:", err);
        Swal.fire("خطأ", "فشل في تحميل المنتجات", "error");
      } finally {
        setLoadingProducts(false);
      }
    };

    const delay = setTimeout(fetchProducts, 300);
    return () => clearTimeout(delay);
  }, [productSearch, showModal]);

  const total = formik.values.items.reduce((acc, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.buyPrice) || 0;
    return acc + qty * price;
  }, 0);

  const totalPayments = formik.values.payments.reduce((acc, p) => {
    return acc + (parseFloat(p.amount) || 0);
  }, 0);

  const totalAfterDiscount = total - (parseFloat(formik.values.discountAmount) || 0);
  const remainingAfterDiscount = totalAfterDiscount - totalPayments;

  const addNewItem = () =>
    formik.setFieldValue("items", [
      ...formik.values.items,
      { productId: "", productName: "", productDescription: "", quantity: 0, buyPrice: 0 },
    ]);
  
  const removeItem = (index) => {
    const newItems = formik.values.items.filter((_, i) => i !== index);
    formik.setFieldValue(
      "items",
      newItems.length ? newItems : [{ productId: "", productName: "", productDescription: "", quantity: 0, buyPrice: 0 }]
    );
  };

  const openProductModal = (index) => {
    setSelectedProductIndex(index);
    setShowModal(true);
    setProductSearch("");
  };

  const selectProduct = (product) => {
    if (selectedProductIndex === null) return;
    formik.setFieldValue(`items[${selectedProductIndex}].productId`, product.id);
    formik.setFieldValue(`items[${selectedProductIndex}].productName`, product.name);
    formik.setFieldValue(`items[${selectedProductIndex}].productDescription`, product.description || "");
    formik.setFieldValue(`items[${selectedProductIndex}].buyPrice`, product.buyPrice || 0);
    setShowModal(false);
  };

  const addNewPayment = () =>
    formik.setFieldValue("payments", [
      ...formik.values.payments,
      { amount: 0, date: new Date().toISOString().slice(0, 10), method: "Cash" },
    ]);
  
  const removePayment = (index) => {
    const newPayments = formik.values.payments.filter((_, i) => i !== index);
    formik.setFieldValue(
      "payments",
      newPayments.length
        ? newPayments
        : [{ amount: 0, date: new Date().toISOString().slice(0, 10), method: "Cash" }]
    );
  };

  if (loading) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <Bars height="80" width="80" color="#f59e0b" ariaLabel="loading" />
          <p className="mt-3" style={{ color: "#64748b", fontSize: "18px" }}>
            جارٍ تحميل تفاصيل الشراء...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="container py-5" style={{ maxWidth: "1000px" }}>
        
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
          <div>
            <h2 style={{ color: "#f59e0b", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
              <i className="bi bi-pencil-square me-2"></i>تعديل فاتورة شراء
            </h2>
            <small style={{ color: "#64748b", fontSize: "14px" }}>
              تحديث تفاصيل طلب الشراء
            </small>
          </div>
          <button
            type="button"
            onClick={() => navigate("/purchases")}
            style={{
              background: "#64748b",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: "6px",
              border: "none",
              fontWeight: "600",
              fontSize: "15px",
              cursor: "pointer",
            }}
          >
            <i className="bi bi-arrow-left me-1"></i> رجوع
          </button>
        </div>

        <form onSubmit={formik.handleSubmit}>
          
          {/* Supplier & Date Section */}
          <div style={cardStyle}>
            <h5 style={sectionTitleStyle}>
              <i className="bi bi-building me-2"></i>معلومات المورد
            </h5>
            
            <div className="row g-3">
              <div className="col-md-8">
                <label style={labelStyle}>المورد</label>
                <select
                  name="supplierId"
                  className={formik.touched.supplierId && formik.errors.supplierId ? "is-invalid" : ""}
                  value={formik.values.supplierId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={inputStyle}
                >
                  <option value="">-- اختر المورد --</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} - {s.details}
                    </option>
                  ))}
                </select>
                {formik.touched.supplierId && formik.errors.supplierId && (
                  <div style={errorStyle}>{formik.errors.supplierId}</div>
                )}
              </div>

              <div className="col-md-4">
                <CustomDatePicker
                  label="تاريخ الشراء"
                  value={formik.values.purchaseDate}
                  onChange={(date) => formik.setFieldValue('purchaseDate', date)}
                  placeholder="اختر تاريخ الشراء"
                />
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div style={cardStyle}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={sectionTitleStyle}>
                <i className="bi bi-box-seam me-2"></i>المنتجات ({formik.values.items.length})
              </h5>
              <button
                type="button"
                onClick={addNewItem}
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                <i className="bi bi-plus-circle me-1"></i> أضف منتج
              </button>
            </div>

            {formik.values.items.map((item, index) => (
              <div key={index} style={itemCardStyle}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span style={{ color: "#2563eb", fontWeight: "600", fontSize: "15px" }}>
                    المنتج رقم #{index + 1}
                  </span>
                  {formik.values.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      style={{
                        background: "#dc2626",
                        color: "#fff",
                        padding: "4px 12px",
                        borderRadius: "4px",
                        border: "none",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      <i className="bi bi-trash me-1"></i> إزالة
                    </button>
                  )}
                </div>

                <div className="mb-3">
                  <label style={labelStyle}>المنتج</label>
                  <div className="d-flex gap-2">
                    <input
                      type="text"
                      value={`${item.productName || "لم يتم اختيار منتج"} ${item.productDescription ? `- ${item.productDescription}` : ""}`}
                      readOnly
                      style={{ ...inputStyle, flex: 1, background: "#f8fafc", cursor: "default" }}
                    />
                    <button
                      type="button"
                      onClick={() => openProductModal(index)}
                      style={{
                        background: "#2563eb",
                        color: "#fff",
                        padding: "10px 20px",
                        borderRadius: "6px",
                        border: "none",
                        fontWeight: "600",
                        fontSize: "14px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <i className="bi bi-search me-1"></i> اختر
                    </button>
                  </div>
                  {formik.touched.items?.[index]?.productId &&
                    formik.errors.items?.[index]?.productId && (
                      <div style={errorStyle}>{formik.errors.items[index].productId}</div>
                    )}
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label style={labelStyle}>الكمية</label>
                    <input
                      name={`items[${index}].quantity`}
                      type="number"
                      min="0"
                      value={item.quantity}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onWheel={(e) => e.target.blur()}
                      style={inputStyle}
                    />
                    {formik.touched.items?.[index]?.quantity &&
                      formik.errors.items?.[index]?.quantity && (
                        <div style={errorStyle}>{formik.errors.items[index].quantity}</div>
                      )}
                  </div>
                  <div className="col-md-6">
                    <label style={labelStyle}>سعر الشراء (جنيه)</label>
                    <input
                      name={`items[${index}].buyPrice`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.buyPrice}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onWheel={(e) => e.target.blur()}
                      style={inputStyle}
                    />
                    {formik.touched.items?.[index]?.buyPrice &&
                      formik.errors.items?.[index]?.buyPrice && (
                        <div style={errorStyle}>{formik.errors.items[index].buyPrice}</div>
                      )}
                  </div>
                </div>

                {/* Item Subtotal */}
                <div style={{ 
                  marginTop: "12px", 
                  padding: "10px", 
                  background: "#f0f9ff", 
                  borderRadius: "6px",
                  borderLeft: "3px solid #2563eb"
                }}>
                  <span style={{ color: "#1e40af", fontWeight: "600", fontSize: "14px" }}>
                    الإجمالي الفرعي: {((item.quantity || 0) * (item.buyPrice || 0)).toFixed(2)} جنيه
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Total Summary */}
          <div style={{
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "20px",
            boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
          }}>
            <div className="d-flex justify-content-between align-items-center">
              <span style={{ color: "#fff", fontSize: "18px", fontWeight: "600" }}>
                <i className="bi bi-calculator me-2"></i>إجمالي قيمة الشراء
              </span>
              <span style={{ color: "#fff", fontSize: "28px", fontWeight: "700" }}>
                {total.toFixed(2)} جنيه
              </span>
            </div>
          </div>

          {/* ✅ Discount Section */}
          <div style={cardStyle}>
            <h5 style={{...sectionTitleStyle, fontSize: "20px", marginBottom: "20px"}}>
              <i className="bi bi-tag me-2"></i>الخصم
            </h5>
            <div className="mb-3" style={{ maxWidth: "400px" }}>
              <label style={{...labelStyle, fontSize: "16px", marginBottom: "10px"}}>قيمة الخصم (جنيه)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                name="discountAmount"
                placeholder="0.00"
                value={formik.values.discountAmount || 0}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                onWheel={(e) => e.target.blur()}
                style={{...inputStyle, padding: "14px 16px", fontSize: "16px"}}
              />
              {formik.touched.discountAmount && formik.errors.discountAmount && (
                <div style={errorStyle}>{formik.errors.discountAmount}</div>
              )}
            </div>

            {/* Summary after discount */}
            <div style={{
              background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
              padding: "24px",
              borderRadius: "10px",
              marginTop: "20px",
              borderLeft: "5px solid #f59e0b",
              boxShadow: "0 2px 8px rgba(245, 158, 11, 0.15)"
            }}>
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ color: "#92400e", fontWeight: "700", fontSize: "20px" }}>
                  الإجمالي بعد الخصم:
                </span>
                <span style={{ color: "#92400e", fontWeight: "800", fontSize: "26px" }}>
                  {totalAfterDiscount.toFixed(2)} جنيه
                </span>
              </div>
            </div>
          </div>

          {/* Payments Section */}
          <div style={cardStyle}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={sectionTitleStyle}>
                <i className="bi bi-cash-stack me-2"></i>المدفوعات
              </h5>
              <button
                type="button"
                onClick={addNewPayment}
                style={{
                  background: "#22c55e",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                <i className="bi bi-plus-circle me-1"></i> أضف دفعة
              </button>
            </div>

            {formik.values.payments.map((p, index) => (
              <div key={index} style={paymentCardStyle}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span style={{ color: "#22c55e", fontWeight: "600", fontSize: "15px" }}>
                    الدفعة رقم #{index + 1}
                  </span>
                  {formik.values.payments.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePayment(index)}
                      style={{
                        background: "#dc2626",
                        color: "#fff",
                        padding: "4px 12px",
                        borderRadius: "4px",
                        border: "none",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      <i className="bi bi-trash me-1"></i> إزالة
                    </button>
                  )}
                </div>

                <div className="row g-3">
                  <div className="col-md-4">
                    <label style={labelStyle}>المبلغ (جنيه)</label>
                    <input
                      name={`payments[${index}].amount`}
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={p.amount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onWheel={(e) => e.target.blur()}
                      style={inputStyle}
                    />
                  </div>

                  <div className="col-md-4">
                    <CustomDatePicker
                      label="تاريخ الدفعة"
                      value={p.date}
                      onChange={(date) => formik.setFieldValue(`payments[${index}].date`, date)}
                      placeholder="اختر تاريخ الدفعة"
                    />
                  </div>

                  <div className="col-md-4">
                    <label style={labelStyle}>طريقة الدفع</label>
                    <select
                      name={`payments[${index}].method`}
                      value={p.method}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={inputStyle}
                    >
                      <option value="Cash">💵 نقدي</option>
                      <option value="VodafoneCash">📱 فودافون كاش</option>
                      <option value="InstaBay">💳 إنستاباي</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}

            {/* Payment Summary */}
            <div style={{
              background: "#f0fdf4",
              padding: "16px",
              borderRadius: "6px",
              marginTop: "16px",
              borderLeft: "3px solid #22c55e"
            }}>
              <span style={{ color: "#166534", fontWeight: "600", fontSize: "14px" }}>
                الإجمالي المدفوع: {totalPayments.toFixed(2)} جنيه
              </span>
              <br />
              <span style={{ color: "#166534", fontWeight: "600", fontSize: "14px" }}>
                المتبقي بعد الخصم: {remainingAfterDiscount.toFixed(2)} جنيه
              </span>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!(formik.dirty && formik.isValid)}
            style={{
              width: "100%",
              background: formik.dirty && formik.isValid
                ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                : "#94a3b8",
              color: "#fff",
              padding: "16px",
              borderRadius: "8px",
              border: "none",
              fontWeight: "700",
              fontSize: "18px",
              cursor: formik.dirty && formik.isValid ? "pointer" : "not-allowed",
              boxShadow: formik.dirty && formik.isValid
                ? "0 4px 12px rgba(245, 158, 11, 0.3)"
                : "none",
              transition: "all 0.3s ease",
            }}
          >
            <i className="bi bi-check-circle me-2"></i>تحديث الطلب
          </button>
        </form>
      </div>

      {/* Product Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "700px",
              maxHeight: "80vh",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: "20px 24px",
              borderBottom: "1px solid #e2e8f0",
              background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
            }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 style={{ color: "#fff", fontWeight: "700", fontSize: "20px", margin: 0 }}>
                  <i className="bi bi-search me-2"></i>اختر المنتج
                </h5>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    cursor: "pointer",
                    fontSize: "20px",
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <div style={{ padding: "20px 24px" }}>
              <input
                type="text"
                placeholder="🔍 ابحث عن المنتجات بالاسم..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                autoFocus
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #bfdbfe",
                  borderRadius: "8px",
                  fontSize: "15px",
                  marginBottom: "16px",
                  outline: "none",
                }}
              />

              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {loadingProducts ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    <div className="spinner-border text-primary" role="status"></div>
                    <p style={{ marginTop: "12px" }}>جارٍ تحميل المنتجات...</p>
                  </div>
                ) : products.length > 0 ? (
                  <div>
                    {products.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => selectProduct(product)}
                        style={{
                          width: "100%",
                          padding: "16px",
                          marginBottom: "8px",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          background: "#fff",
                          textAlign: "left",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f0f9ff";
                          e.currentTarget.style.borderColor = "#2563eb";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#fff";
                          e.currentTarget.style.borderColor = "#e2e8f0";
                        }}
                      >
                        <div style={{ fontWeight: "600", color: "#1e293b", marginBottom: "4px" }}>
                          {product.name}
                        </div>
                        <div style={{ fontSize: "14px", color: "#64748b" }}>
                          {product.description}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    <i className="bi bi-inbox" style={{ fontSize: "48px", marginBottom: "12px" }}></i>
                    <p>لم يتم العثور على منتجات</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ✅ Styles
const cardStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  padding: "24px",
  marginBottom: "20px",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
};

const sectionTitleStyle = {
  color: "#1e293b",
  fontWeight: "700",
  fontSize: "18px",
  marginBottom: "20px",
};

const labelStyle = {
  color: "#475569",
  fontSize: "14px",
  fontWeight: "600",
  marginBottom: "6px",
  display: "block",
};

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  border: "1px solid #cbd5e1",
  borderRadius: "6px",
  fontSize: "15px",
  color: "#1e293b",
  outline: "none",
  transition: "all 0.2s",
};

const errorStyle = {
  color: "#dc2626",
  fontSize: "13px",
  marginTop: "4px",
  fontWeight: "500",
};

const itemCardStyle = {
  background: "#f8fafc",
  border: "2px solid #e0e7ff",
  borderRadius: "8px",
  padding: "16px",
  marginBottom: "12px",
};

const paymentCardStyle = {
  background: "#f0fdf4",
  border: "2px solid #bbf7d0",
  borderRadius: "8px",
  padding: "16px",
  marginBottom: "12px",
};